import type { ICountry, IProvince } from '@/entities/country'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useUpdateCountryBannerMutation, useUpdateProvinceBannerMutation } from '@/entities/country'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'
import { showToast } from '@/shared/libs/toast'

type BannerTarget =
  | { kind: 'country'; country: ICountry }
  | { kind: 'province'; province: IProvince }

interface UseLocationBannerResult {
  target: BannerTarget | null
  saving: boolean
  editCountry: (country: ICountry) => void
  editProvince: (province: IProvince) => void
  close: () => void
  previewUrl: string | null
  openPreview: (url: string) => void
  closePreview: () => void
  save: (banner: string | null) => Promise<void>
}

/**
 * Tracks which location's banner is being edited and persists it through master-service.
 */
export function useLocationBanner(): UseLocationBannerResult {
  const { t } = useTranslation()
  const [target, setTarget] = useState<BannerTarget | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const openPreview = useCallback((url: string) => setPreviewUrl(url), [])
  const closePreview = useCallback(() => setPreviewUrl(null), [])
  const countryMutation = useUpdateCountryBannerMutation()
  const provinceMutation = useUpdateProvinceBannerMutation()

  // * VI: Giữ nguyên tham chiếu hàm để cột của bảng không bị tạo lại mỗi lần render | EN: Stable refs so table columns are not rebuilt on every render
  const editCountry = useCallback(
    (country: ICountry) => setTarget({ kind: 'country', country }),
    [],
  )
  const editProvince = useCallback(
    (province: IProvince) => setTarget({ kind: 'province', province }),
    [],
  )
  const close = useCallback(() => setTarget(null), [])

  async function save(banner: string | null): Promise<void> {
    if (!target) return
    try {
      if (target.kind === 'country') {
        await countryMutation.mutateAsync({
          countryId: target.country.id,
          payload: { banner },
        })
      } else {
        await provinceMutation.mutateAsync({
          countryId: target.province.countryId,
          provinceId: target.province.id,
          payload: { banner },
        })
      }
      showToast.success(t('location.bannerSaved'))
      setTarget(null)
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  return {
    target,
    saving: countryMutation.isPending || provinceMutation.isPending,
    editCountry,
    editProvince,
    close,
    previewUrl,
    openPreview,
    closePreview,
    save,
  }
}
